import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { CalendarIcon, LoaderCircleIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { calculateExpectedCostForScraping } from '../../helpers/calculate-expected-cost-for-scraping';

import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/public-filter-constants';
import { Button } from '@/shared/components/shadcn/button';
import { Calendar } from '@/shared/components/shadcn/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/shadcn/form';
import { Input } from '@/shared/components/shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/shadcn/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { Textarea } from '@/shared/components/shadcn/textarea';
import { cn } from '@/shared/utils/cn';
import { DATE_FORMATS } from '@/shared/utils/date/date-formats';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';
import { scrapingQueryKeys } from '../../model/scraping-query-keys';
import { SCRAPING_INTENT_MODE, SCRAPING_INTENT_SELECTOR_TYPE } from '../../types';
import { queryClient } from '@/shared/query-client';
import { createScrapingIntent } from '../../apis';

const BaseSchema = z.object({
  name: z.string(),
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  item_per_day: z.number().int().min(1),
  keyword: z.string(),
  exact_match: z.boolean(),
  memo: z.string().optional(),
});

const OnetimeSchema = BaseSchema.extend({
  mode: z.literal(SCRAPING_INTENT_MODE.ONETIME),
  start_date: z.date({
    error: 'Start date is required.',
  }),
  end_date: z.date({
    error: 'End date is required.',
  }),
});

const PeriodicSchema = BaseSchema.extend({
  mode: z.literal(SCRAPING_INTENT_MODE.PERIODIC),
});

const FormSchema = z.discriminatedUnion('mode', [OnetimeSchema, PeriodicSchema]);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  onConfirm: () => void;
}

export function GoogleSearchScrapingDialog({ isOpen, onClose, onExit, onConfirm }: Props) {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      platform: SOURCE_PLATFORM_TYPE.TIKTOK,
      keyword: '',
      exact_match: false,
      item_per_day: 20,
      start_date: undefined,
      end_date: new Date(),
      mode: SCRAPING_INTENT_MODE.ONETIME,
      memo: '',
    },
  });

  const startDateToScrape = form.watch('start_date');
  const endDateToScrape = form.watch('end_date');
  const itemsPerDay = form.watch('item_per_day');
  const mode = form.watch('mode');
  const expectedCost = useMemo(() => {
    if (!startDateToScrape || !endDateToScrape) {
      return 0;
    }

    const startDate = dayjs(startDateToScrape);
    const endDate = dayjs(endDateToScrape);

    const daysDiff = endDate.diff(startDate, 'day') + 1;
    if (daysDiff <= 0) {
      return 0;
    }

    const expectedItemCountToScrape = daysDiff * (itemsPerDay || 20);

    return calculateExpectedCostForScraping(expectedItemCountToScrape);
  }, [startDateToScrape, endDateToScrape, itemsPerDay]);

  const handleSubmit = async (data: z.infer<typeof FormSchema>) => {
    console.log(data);
    // Additional validation for date comparison
    if (data.mode === SCRAPING_INTENT_MODE.ONETIME) {
      if (data.start_date > data.end_date) {
        form.setError('end_date', { message: 'End date must be after start date.' });
        return;
      }
    }

    setIsCreating(true);
    try {
      await createScrapingIntent({
        name: data.name,
        mode: data.mode,
        payload: {
          platform: data.platform,
          selector: {
            type: SCRAPING_INTENT_SELECTOR_TYPE.KEYWORD,
            keyword: data.keyword,
            exact_match: data.exact_match,
          },
          limit: {
            item_per_day: data.item_per_day,
            date_range:
              data.mode === SCRAPING_INTENT_MODE.ONETIME
                ? {
                    start_date: dayjs(data.start_date).toISOString(),
                    end_date: dayjs(data.end_date).toISOString(),
                  }
                : undefined,
          },
          memo: data.memo,
        },
      });
      queryClient.invalidateQueries({
        queryKey: scrapingQueryKeys.intentList({
          type: SCRAPING_INTENT_SELECTOR_TYPE.KEYWORD,
        }),
      });

      onConfirm();
      onClose();
      onExit();
      form.reset();
    } catch (error) {
      await showErrorDialog({ error });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] w-fit min-w-100 overflow-auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <DialogHeader>
              <DialogTitle>Create new</DialogTitle>
              <DialogDescription>Create scraping request to attain datapoints</DialogDescription>
            </DialogHeader>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter Intent name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="platform"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Platform</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={SOURCE_PLATFORM_TYPE.TIKTOK}>TikTok</SelectItem>
                      <SelectItem value={SOURCE_PLATFORM_TYPE.INSTAGRAM}>Instagram</SelectItem>
                      <SelectItem value={SOURCE_PLATFORM_TYPE.YOUTUBE}>YouTube</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="keyword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Keyword</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter keyword" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* <FormField
                control={form.control}
                name="exact_match"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-y-0 space-x-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>⚠️ Fuzzy search</FormLabel>
                      <FormDescription className="text-xs">
                        기본적으로 Keyword에 "가 붙습니다. 이 옵션을 체크하면 안 붙입니다. 그러면
                        Garbage가 많이 나옵니다.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              /> */}

              <FormField
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mode</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select run mode" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={SCRAPING_INTENT_MODE.ONETIME}>One-time only</SelectItem>
                        <SelectItem value={SCRAPING_INTENT_MODE.PERIODIC}>Periodic only</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="item_per_day"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Items per day</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="20"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                      />
                    </FormControl>
                    <p className="text-muted-foreground text-sm">Range: 1-300 items per day</p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {mode !== SCRAPING_INTENT_MODE.PERIODIC && (
                <>
                  <FormField
                    control={form.control}
                    name="start_date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <label className="text-sm font-medium">Start Date</label>
                        <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={'outline'}
                                className={cn(
                                  'w-60 pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground',
                                )}
                              >
                                {field.value ? (
                                  dayjs(field.value).format(DATE_FORMATS.base)
                                ) : (
                                  <span>Pick a start date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0"
                            align="start"
                            side="bottom"
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(date) => {
                                field.onChange(date);
                                setIsDatePickerOpen(false);
                              }}
                              disabled={(date) =>
                                date > new Date() || date < new Date('1900-01-01')
                              }
                              captionLayout="dropdown"
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="end_date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <label className="text-sm font-medium">End Date</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={'outline'}
                                className={cn(
                                  'w-60 pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground',
                                )}
                              >
                                {field.value ? (
                                  dayjs(field.value).format(DATE_FORMATS.base)
                                ) : (
                                  <span>Pick an end date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0"
                            align="start"
                            side="bottom"
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date > new Date() || date < new Date('1900-01-01')
                              }
                              captionLayout="dropdown"
                            />
                          </PopoverContent>
                        </Popover>
                        <p className="text-muted-foreground text-sm">
                          Expected cost: ${expectedCost}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="memo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Memo (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any notes about this scraping job..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button variant="default" type="submit" disabled={isCreating}>
                {isCreating && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                {isCreating ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
